import { useCallback, useEffect, useMemo } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import {
  BulkEditListingPriceDialog,
  BulkEditListingPriceDialogProps,
} from 'src/dialogs/BulkEdits/BulkEditListingPriceDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserCanSetPrice } from 'src/hooks/useUserHasListingPermissions';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  CommandOptionType,
  Event,
  ListingDetails,
  ListingPriceAdjustmentType,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_LISTING_PRICES_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchChangeListingPrice } from './LaunchChangeListingPrice';

export const useBulkChangeListingPriceAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const bulkUpdateListingsPricesUpdateKey = event
    ? `listingPrices-event-${event.virtId}`
    : INVENTORY_BULK_UPDATE_LISTING_PRICES_UPDATE_KEY;

  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();

  const {
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();

  const { showErrorDialog } = useClientLogContext();

  const { openDialog: openBulkEditDialog, closeDialog: closeBulkEditDialog } =
    useDialog<BulkEditListingPriceDialogProps>(
      DialogId.BulkEditListingPrice,
      BulkEditListingPriceDialog
    );

  const onSubmitChangePriceByPercent = useCallback(
    async (
      priceChangePercentage: number | undefined,
      priceChangeAbsolute: number | undefined,
      increasePrice: boolean,
      adjustmentType: ListingPriceAdjustmentType,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateListingsPricesPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateListingsPricesPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            let succeeded: BulkEditProgress | undefined;
            if (priceChangePercentage !== undefined) {
              succeeded = await new BulkEditListingClient(
                activeAccountWebClientConfig
              ).bulkUpdateListingsPricesByPercent(
                preview!.preview,
                (increasePrice
                  ? 100 + priceChangePercentage
                  : 100 - priceChangePercentage) /
                  100 /* we're sending the percent back in 0 - 1 ratio */,
                bulkUpdateListingsPricesUpdateKey,
                supportBackgroundProcess,
                adjustmentType
              );
            } else if (priceChangeAbsolute !== undefined) {
              succeeded = await new BulkEditListingClient(
                activeAccountWebClientConfig
              ).bulkUpdateListingsPrices(
                preview!.preview,
                increasePrice ? priceChangeAbsolute : -priceChangeAbsolute,
                bulkUpdateListingsPricesUpdateKey,
                supportBackgroundProcess,
                adjustmentType
              );
            }

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkEditDialog();
            }
          },
          (error) => {
            showErrorDialog('onSubmitChangePrice', error, {
              trackErrorData: {
                priceChangePercentage,
                priceChangeAbsolute,
                increasePrice,
                preview,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQueryWithEventIds,
      showErrorDialog,
      bulkUpdateListingsPricesUpdateKey,
      closeBulkEditDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const openDialog = useCallback(
    (e: React.MouseEvent, increasePrice: boolean) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }
      openBulkEditDialog({
        updateKey: bulkUpdateListingsPricesUpdateKey,
        increasePrice,
        onOkay: onSubmitChangePriceByPercent,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditDialog,
      });
    },
    [
      bulkUpdateListingsPricesUpdateKey,
      closeBulkEditDialog,
      event,
      onSubmitChangePriceByPercent,
      openBulkEditDialog,
      setIsLoading,
      setListItemExpansion,
    ]
  );

  const openIncreasePriceDialog = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }
    openBulkEditDialog({
      updateKey: bulkUpdateListingsPricesUpdateKey,
      increasePrice: true,
      onOkay: onSubmitChangePriceByPercent,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditDialog,
    });
  });

  const openDecreatePriceDialog = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }
    openBulkEditDialog({
      updateKey: bulkUpdateListingsPricesUpdateKey,
      increasePrice: false,
      onOkay: onSubmitChangePriceByPercent,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditDialog,
    });
  });

  // Register increase price command with the context
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const hasEditPricingPermission = useUserCanSetPrice(undefined, true);

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditIncreasePrice,
        onSelect: openIncreasePriceDialog,
      },
      disabled: !hasEditPricingPermission,
    });

    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditDecreasePrice,
        onSelect: openDecreatePriceDialog,
      },
      disabled: !hasEditPricingPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditIncreasePrice,
        disabled: !hasEditPricingPermission,
      });
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditDecreasePrice,
        disabled: !hasEditPricingPermission,
      });
    };
  }, [
    hasEditPricingPermission,
    openDecreatePriceDialog,
    openIncreasePriceDialog,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasEditPricingPermission) {
      return [];
    }

    return [
      {
        title: ContentId.IncreasePrice,
        onClick: (e: React.MouseEvent) => {
          openDialog(e, true);
        },
        disabled: isLoading,
      },
      {
        title: ContentId.DecreasePrice,
        onClick: (e: React.MouseEvent) => {
          openDialog(e, false);
        },
        disabled: isLoading,
      },
    ];
  }, [hasEditPricingPermission, isLoading, openDialog]);

  return {
    dropDown: (
      <LaunchChangeListingPrice
        key="ChangeListingPrice"
        onClick={openDialog}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
